import { UPDATE_LOAN_BALANCE } from '@common/constants/webSockets'
import { IClientPortalInfo } from '@common/interfaces/client'
import { IUser } from '@common/interfaces/user'
import { combineReducers } from 'redux'
import { loadingArrayReducer } from '../../helpers/helpers'
import { readStoredFlag } from '../../helpers/storedFlag'
import { IAction } from '../apiMiddleware'
import { VERIFY_TWO_FACTOR_SUCCESS } from '../twoFactor/actions'
import * as actions from './actions'

// Shape of `action.data` for LOGIN_SUCCESS. Two-factor users get a partial
// response carrying only `requiresTwoFactor`/`tempUserId` (see
// server/src/services/auth/Login.ts); non-2FA users get a jwt + user instead,
// so both fields are optional here.
interface ILoginResponse {
  requiresTwoFactor?: boolean
  tempUserId?: string
}

export default combineReducers({
  isInitialized(state: boolean = false, action: IAction): boolean {
    switch (action.type) {
      case actions.PROFILE_SUCCESS:
      case actions.PROFILE_FAILURE:
      case actions.PROFILE_HYDRATED:
        return true
      default:
        return state
    }
  },
  loadingArray: loadingArrayReducer([actions.prefix]),
  user(state: IUser = null, action: IAction): IUser {
    switch (action.type) {
      // Never over a profile the network has already delivered.
      case actions.PROFILE_HYDRATED:
        return state ?? (action.data as IUser)
      case actions.PROFILE_SUCCESS:
        return action.data as IUser
      // Self-updates answer with the stored row, which knows nothing of the companies a client
      // user can switch between, nor (the key column being unselected) whether a Fireflies key is
      // saved — keep what `/users/me` said about those unless the response says otherwise.
      case actions.UPDATE_PROFILE_SUCCESS:
      case actions.UPDATE_NOTIFICATION_SETTINGS_SUCCESS:
      case actions.UPDATE_DISPLAY_SETTINGS_SUCCESS:
      case actions.UPDATE_ARC_SETTINGS_SUCCESS:
      case actions.REVERT_LEARNED_PROFILE_SUCCESS:
      case actions.UPDATE_AIM_PROFILE_SUCCESS: {
        const data = action.data as IUser
        if (!state) return data
        return {
          ...data,
          clientMemberships: state.clientMemberships,
          hasFirefliesApiKey: data.hasFirefliesApiKey ?? state.hasFirefliesApiKey,
        }
      }
      // Voice & Tone read/update return only the two voice-tone fields (not the
      // full user), so merge them into the existing user rather than replacing.
      case actions.READ_VOICE_TONE_SUCCESS:
      case actions.UPDATE_VOICE_TONE_SUCCESS: {
        if (!state) return state
        const data = action.data as {
          aiVoiceTone: string | null
          aiVoiceToneGeneratedAt: string | Date | null
        }
        return {
          ...state,
          aiVoiceTone: data.aiVoiceTone,
          aiVoiceToneGeneratedAt: data.aiVoiceToneGeneratedAt,
        }
      }
      case actions.LOGOUT_SUCCESS:
      case actions.SESSION_EXPIRED:
        return null
      default:
        return state
    }
  },
  // Whether a Voice & Tone generation job is in flight server-side. Sourced from
  // the Read response (queue job state), so it survives a page refresh — the box's
  // local "regenerating" state does not.
  voiceToneRegenerating(state: boolean = false, action: IAction): boolean {
    switch (action.type) {
      case actions.READ_VOICE_TONE_SUCCESS:
        return Boolean((action.data as { isRegenerating?: boolean }).isRegenerating)
      default:
        return state
    }
  },
  isConfirmed(state: boolean = false, action: IAction): boolean {
    switch (action.type) {
      case actions.CONFIRM_SUCCESS:
        return true
      default:
        return state
    }
  },
  isChangedPwd(state: boolean = false, action: IAction): boolean {
    switch (action.type) {
      case actions.RESET_PASSWORD_REQUEST:
      case actions.RESET_PASSWORD_FAILURE:
        return false
      case actions.RESET_PASSWORD_SUCCESS:
        return true
      default:
        return state
    }
  },
  isRecoveryRequested(state: boolean = false, action: IAction): boolean {
    switch (action.type) {
      case actions.PASSWORD_RECOVERY_REQUEST:
      case actions.PASSWORD_RECOVERY_FAILURE:
        return false
      case actions.PASSWORD_RECOVERY_SUCCESS:
        return true
      default:
        return state
    }
  },
  isPasswordSetup(state: boolean = false, action: IAction): boolean {
    switch (action.type) {
      case actions.PROCESS_INVITATION_SUCCESS:
        return true
      default:
        return state
    }
  },
  invitedUser(state: IUser = null, action: IAction): IUser {
    switch (action.type) {
      case actions.PROCESS_INVITATION_SUCCESS:
        return action.data as IUser
      default:
        return state
    }
  },
  error(state: object | null = null, action: IAction & { error: object }): object | null {
    switch (action.type) {
      case actions.LOGIN_FAILURE:
      case actions.GOOGLE_AUTH_FAILURE:
      case actions.LINK_GOOGLE_FAILURE:
      case actions.UNLINK_GOOGLE_FAILURE:
      case actions.MICROSOFT_AUTH_FAILURE:
      case actions.LINK_MICROSOFT_FAILURE:
      case actions.UNLINK_MICROSOFT_FAILURE:
      case actions.LOGOUT_FAILURE:
      case actions.PASSWORD_RECOVERY_FAILURE:
      case actions.RESET_PASSWORD_FAILURE:
        return action.error
      default:
        return state
    }
  },
  clientInfo(state: IClientPortalInfo = null, action: IAction): IClientPortalInfo {
    switch (action.type) {
      case actions.CLIENT_INFO_SUCCESS:
      case actions.CLIENT_INFO_RELOAD_SUCCESS:
        return action.data as IClientPortalInfo
      case UPDATE_LOAN_BALANCE: {
        const data = action.data as { clientId: string; loanBalance: number }
        if (!state || state.id !== data.clientId) {
          return state
        }
        return {
          ...state,
          currentAmount: data.loanBalance,
        }
      }
      default:
        return state
    }
  },

  isSidebarOpen(
    state: boolean = readStoredFlag(actions.SIDEBAR_PINNED_STORAGE_KEY, false),
    action: IAction,
  ): boolean {
    switch (action.type) {
      case actions.TOGGLE_SIDEBAR:
        return !state
      case actions.OPEN_SIDEBAR:
        return true
      default:
        return state
    }
  },

  requiresTwoFactor(state: boolean = false, action: IAction): boolean {
    switch (action.type) {
      case actions.LOGIN_SUCCESS:
        return (action.data as ILoginResponse).requiresTwoFactor || false
      case actions.LOGIN_REQUEST:
      case actions.LOGIN_FAILURE:
      case VERIFY_TWO_FACTOR_SUCCESS:
        return false
      default:
        return state
    }
  },

  tempUserId(state: string | null = null, action: IAction): string | null {
    switch (action.type) {
      case actions.LOGIN_SUCCESS:
        return (action.data as ILoginResponse).tempUserId || null
      case actions.LOGIN_REQUEST:
      case actions.LOGIN_FAILURE:
      case VERIFY_TWO_FACTOR_SUCCESS:
        return null
      default:
        return state
    }
  },
})
